import React, { useEffect } from 'react';
import {
  Home, Info, Sparkles, BookOpen, HelpCircle, Mail, ShieldCheck,
  FileText, X, ExternalLink, ChevronRight
} from 'lucide-react';
import { BrandLogo } from '../../features/shared/components/BrandLogo';
import { APP_VERSION, SITE_TAGLINE } from '../../config/site';
import { App } from '@capacitor/app';

interface AppDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
}

interface DrawerMenuItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

const DRAWER_ITEMS: DrawerMenuItem[] = [
  { label: 'Home', path: '/', icon: Home },
  { label: 'About Us', path: '/about', icon: Info },
  { label: 'App Features', path: '/features', icon: Sparkles },
  { label: 'Blog / Updates', path: '/blog', icon: BookOpen },
  { label: 'FAQ', path: '/faq', icon: HelpCircle },
  { label: 'Contact Us', path: '/contact', icon: Mail },
  { label: 'Privacy Policy', path: '/privacy-policy', icon: ShieldCheck },
  { label: 'Terms & Conditions', path: '/terms', icon: FileText }
];

export const AppDrawer: React.FC<AppDrawerProps> = ({
  isOpen,
  onClose,
  currentPath,
  onNavigate
}) => {
  // Close drawer on Android hardware back button
  useEffect(() => {
    if (!isOpen) return;

    let backHandler: any;
    const registerBack = async () => {
      backHandler = await App.addListener('backButton', () => {
        onClose();
      });
    };
    void registerBack();

    return () => {
      if (backHandler && typeof backHandler.remove === 'function') {
        backHandler.remove();
      }
    };
  }, [isOpen, onClose]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[160] flex animate-in fade-in duration-200">
      {/* Dim Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity"
        aria-hidden="true"
      />

      {/* Slide-in Navigation Panel */}
      <div className="relative w-80 max-w-[85vw] bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col z-10 border-r border-slate-200 dark:border-slate-800 animate-in slide-in-from-left duration-250">

        {/* Drawer Header */}
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-900">
          <div className="flex flex-col gap-1">
            <BrandLogo size="md" />
            <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">
              {SITE_TAGLINE}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation drawer"
            className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Menu Items */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1 text-sm font-semibold">
          {DRAWER_ITEMS.map(item => {
            const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));
            const ItemIcon = item.icon;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => {
                  onNavigate(item.path);
                  onClose();
                }}
                className={`w-full min-h-[44px] flex items-center justify-between px-3.5 py-2.5 rounded-2xl transition-all cursor-pointer ${
                  isActive
                    ? 'bg-blue-900 text-white shadow-md font-bold'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <ItemIcon className={`w-4 h-4 ${isActive ? 'text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`} />
                  <span>{item.label}</span>
                </div>
                <ChevronRight className={`w-4 h-4 ${isActive ? 'text-white/70' : 'text-slate-400'}`} />
              </button>
            );
          })}
        </nav>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 text-xs text-slate-500 dark:text-slate-400 space-y-1">
          <div className="flex items-center justify-between font-bold">
            <span>YeTryKar Platform</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-900">
              {APP_VERSION}
            </span>
          </div>
          <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
            © {new Date().getFullYear()} YeTryKar. All rights reserved.
          </p>
        </div>

      </div>
    </div>
  );
};
